import React, { Fragment } from 'react';

import classNames from 'classnames';

import { percentBandVariant } from './unallocatedSummaryTree';

import { decimal } from 'Helpers/currencyFormat';
import { formatFigure, ZERO_DASH } from 'Helpers/formatService';
import { MARGIN_NO_DIVISOR } from 'Helpers/marginPercentage';

import Badge from 'CommonComponents/Badge';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';

/**
 * Department > Cost Centre > Project in a single hierarchy column, with the
 * numeric columns to its right. Indentation is by level so the depth of a row is
 * readable without reading its name.
 */
const INDENT_REM = 0.75;

export const DEPARTMENT_LEVEL = 0;
export const COSTCENTRE_LEVEL = 1;

const indent = (level) => ({ paddingInlineStart: `${level * INDENT_REM}rem` });

/**
 * The percentage cell. A project with no value has nothing to divide by — the
 * no-divisor dash, uncoloured, rather than a badge claiming 0%.
 */
function UnallocatedPercentBadge({ percent, balance }) {
    if (percent === null || percent === undefined) {
        return MARGIN_NO_DIVISOR;
    }

    const text = percentage(percent);

    // A zero is the plain zero dash in the cell colour, like every other table.
    if (text === ZERO_DASH) {
        return text;
    }

    return <Badge variant={percentBandVariant(percent, balance)}>{text}</Badge>;
}

const DISPLAY_PRECISION = 2;

/**
 * Money at a FIXED 2dp, with the negative in accounting parentheses — matching the
 * export, whose value binder formats every numeric cell as #,##0.00.
 *
 * formatToFixedMoneyWithLocale's precision argument cannot do this on its own: it only
 * feeds an intermediate toFixed, and the grouping step behind it runs Intl.NumberFormat
 * with no fraction-digit bounds, which caps at three and drops trailing zeros. A balance
 * of 2517725.50 came out as 2,517,725.5 and a zero balance as 0, against 2,517,725.50
 * and 0.00 in the file. On screen a zero shows as "–" and nothing entered as blank.
 */
export const money = (value) =>
    formatFigure(
        value,
        (amount, precision) => {
            const formatted = decimal(Math.abs(Number(amount)), precision);

            return amount < 0 ? `(${formatted})` : formatted;
        },
        { precision: DISPLAY_PRECISION },
    );

/**
 * The percentage at the same fixed 2dp, in the same locale as the money beside it.
 *
 * formatToFixedLocaleString is a bare Number().toFixed() with a hard '.' separator — its
 * own docblock calls it the intermediate rather than a display format — so on a
 * comma-decimal currency it would put a dot in this column and a comma in the next, in
 * the same row.
 */
export const percentage = (value) =>
    formatFigure(value, (percent, precision) => decimal(Number(percent), precision), { precision: DISPLAY_PRECISION, suffix: '%' });

function ValueCells({ projectValue, balance, percent }) {
    return (
        <>
            <td className="inbud-numeric-cell">{money(projectValue)}</td>
            <td className="inbud-numeric-cell">{money(balance)}</td>
            <td className="inbud-numeric-cell">
                <UnallocatedPercentBadge percent={percent} balance={balance} />
            </td>
        </>
    );
}

/**
 * A department or cost-centre row: name, link to the budget screen it summarises,
 * chevron, and the group's own subtotals. Clicking anywhere on the row toggles it;
 * the name stays a link to the underlying screen.
 */
function GroupRow({ level, group, expanded, onToggle }) {
    return (
        <tr
            className={classNames('tw-cursor-pointer tw-font-semibold', {
                '!tw-bg-charcoal-15': level === DEPARTMENT_LEVEL,
                '!tw-bg-charcoal-10': level === COSTCENTRE_LEVEL,
            })}
            onClick={onToggle}
        >
            <td>
                <div className="tw-flex tw-items-center tw-gap-x-1.5" style={indent(level)}>
                    {/* The whole row toggles, so the button must not let its own click
                        bubble up to the row handler and toggle a second time. */}
                    <button
                        type="button"
                        className="tw-flex tw-w-1.5 tw-shrink-0 tw-items-center tw-border-0 tw-bg-transparent tw-p-0"
                        aria-expanded={expanded}
                        aria-label={expanded ? $t('Collapse') : $t('Expand')}
                        onClick={(event) => {
                            event.stopPropagation();
                            onToggle();
                        }}
                    >
                        {expanded ? (
                            <ChevronDownIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                        ) : (
                            <ChevronRightIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                        )}
                    </button>
                    <a href={group.url} onClick={(event) => event.stopPropagation()}>
                        {group.name}
                    </a>
                </div>
            </td>
            <ValueCells projectValue={group.projects_total} balance={group.total} percent={group.unallocated_percent} />
        </tr>
    );
}

function ProjectRow({ level, project }) {
    return (
        <tr className="!tw-bg-white">
            <td>
                <div style={indent(level)}>{project.name}</div>
            </td>
            <ValueCells projectValue={project.project_total} balance={project.total} percent={project.unallocated_percent} />
        </tr>
    );
}

/**
 * One department and, while it is expanded, its cost centres and the projects
 * that sit directly under it.
 *
 * Every group starts open, so `collapsed` holds only the keys the user has closed.
 */
export default function UnallocatedExpensesIndexDepartmentBlock({ department, collapsed, onToggleDepartment, onToggleCostcentre }) {
    const departmentExpanded = !collapsed[`dep:${department.id}`];

    return (
        <>
            <GroupRow level={DEPARTMENT_LEVEL} group={department} expanded={departmentExpanded} onToggle={() => onToggleDepartment(department.id)} />
            {departmentExpanded && (
                <>
                    {department.costcenters.map((costcenter) => {
                        const costcentreExpanded = !collapsed[`cc:${costcenter.id}`];

                        return (
                            <Fragment key={costcenter.id}>
                                <GroupRow
                                    level={COSTCENTRE_LEVEL}
                                    group={costcenter}
                                    expanded={costcentreExpanded}
                                    onToggle={() => onToggleCostcentre(costcenter.id)}
                                />
                                {costcentreExpanded &&
                                    costcenter.projects.map((project) => <ProjectRow key={project.id} level={COSTCENTRE_LEVEL + 1} project={project} />)}
                            </Fragment>
                        );
                    })}
                    {department.projects.map((project) => (
                        <ProjectRow key={project.id} level={COSTCENTRE_LEVEL} project={project} />
                    ))}
                </>
            )}
        </>
    );
}
